import { memo, type ReactNode, useCallback } from 'react'
import { clsx } from 'clsx'

import css from './Toggle.module.scss'

import Field from '@/components/Field'
import { useFieldIds } from '@/components/Field/useFieldIds'

interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  hint?: ReactNode
  error?: string
  disabled?: boolean
  /** Optional explicit id, matching every other primitive. */
  id?: string
  className?: string
}

function Toggle({ checked, onChange, label, hint, error, disabled = false, id, className }: ToggleProps) {
  const { controlId, hintId, errorId, describedBy } = useFieldIds({
    id,
    hasHint: !!hint,
    hasError: !!error
  })

  const handleChange = useCallback(() => {
    if (!disabled) onChange(!checked)
  }, [checked, disabled, onChange])

  return (
    <Field
      className={clsx('Toggle', css.root, disabled && css.disabled, className)}
      controlId={controlId}
      label={label}
      hint={hint}
      hintId={hintId}
      error={error}
      errorId={errorId}
      layout="inline"
    >
      <span className={css.switch}>
        <input
          id={controlId}
          type="checkbox"
          className={css.input}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={describedBy}
        />
        <span className={clsx(css.track, checked && css.on)}>
          <span className={css.thumb} />
        </span>
      </span>
    </Field>
  )
}

export default memo(Toggle)
